// frontend/src/components/notifications/NotificationEventTypeManager.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axios from '../../axiosConfig';
import notificationService from '../../services/notificationService';
import {
    Bell, Calendar, User, AlertTriangle, ToggleLeft, ToggleRight,
    Info, Lock, Unlock, Settings, Clock, ChevronDown, ChevronUp, Save
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { ThemedCard, ThemedInput, ThemedButton } from '../themed';

/**
 * Event Type Management Component
 * Allows entity admins to enable/disable notification event types
 * Respects system-level control and shows hierarchical status
 */
const NotificationEventTypeManager = ({ entityId, manageableEventTypes, userRole, onEventTypesUpdate }) => {
    const { t } = useTranslation();
    const { background, text, border, cn } = useTheme();
    const [loading, setLoading] = useState(true);
    const [eventTypes, setEventTypes] = useState([]);
    const [groupedEvents, setGroupedEvents] = useState({});
    const [toggling, setToggling] = useState({});
    // State for advance time settings editing
    const [expandedEventKey, setExpandedEventKey] = useState(null);
    const [advanceTimeForm, setAdvanceTimeForm] = useState({});
    const [savingAdvanceTime, setSavingAdvanceTime] = useState({});

    useEffect(() => {
        // Refresh local state whenever the parent supplies a new manageable list.
        try {
            setLoading(true);

            const normalizedEvents = manageableEventTypes || [];
            setEventTypes(normalizedEvents);

            const grouped = normalizedEvents.reduce((acc, event) => {
                const category = event.category || 'other';
                if (!acc[category]) {
                    acc[category] = [];
                }
                acc[category].push(event);
                return acc;
            }, {});

            setGroupedEvents(grouped);
        } catch (error) {
            console.error('Error parsing manageable event types:', error);
            toast.error(t('errorLoadingEventTypes'));
        } finally {
            setLoading(false);
        }
    }, [manageableEventTypes, t]);

    /**
     * Toggle event type enabled status
     */
    const toggleEventType = async (eventKey, currentEnabled) => {
        try {
            setToggling(prev => ({ ...prev, [eventKey]: true }));

            const response = await axios.put(
                `/api/notification-settings/${entityId}/event-types/${eventKey}/toggle`,
                { is_enabled: !currentEnabled }
            );

            if (response.data.success) {
                // Update local state
                setEventTypes(prev => prev.map(event =>
                    event.event_key === eventKey
                        ? { ...event, is_enabled: !currentEnabled, is_available: !currentEnabled }
                        : event
                ));

                // Update grouped events
                setGroupedEvents(prev => {
                    const updated = { ...prev };
                    Object.keys(updated).forEach(category => {
                        updated[category] = updated[category].map(event =>
                            event.event_key === eventKey
                                ? { ...event, is_enabled: !currentEnabled, is_available: !currentEnabled }
                                : event
                        );
                    });
                    return updated;
                });

                toast.success(t('eventTypeUpdated'));

                notificationService.clearCacheByPattern(`menu_visibility_${entityId}`);
                notificationService.clearCacheByPattern(`event_types_${entityId}`);

                if (typeof onEventTypesUpdate === 'function') {
                    await onEventTypesUpdate();
                }
            }
        } catch (error) {
            console.error('Error toggling event type:', error);

            if (error.response?.data?.system_active === false) {
                toast.error(t('eventTypeDisabledAtSystemLevel'));
            } else {
                toast.error(t('errorUpdatingEventType'));
            }
        } finally {
            setToggling(prev => ({ ...prev, [eventKey]: false }));
        }
    };

    /**
     * Toggle advance time settings expansion for an event type
     * @param {Object} event - Event type object
     */
    const toggleAdvanceTimeExpand = (event) => {
        if (expandedEventKey === event.event_key) {
            // Collapse
            setExpandedEventKey(null);
            setAdvanceTimeForm({});
        } else {
            // Expand and initialize form with current values
            setExpandedEventKey(event.event_key);
            setAdvanceTimeForm({
                supports_advance_time: event.supports_advance_time === 1,
                min_advance_minutes: event.min_advance_minutes || '',
                max_advance_minutes: event.max_advance_minutes || '',
                default_advance_minutes: event.default_advance_minutes || ''
            });
        }
    };

    /**
     * Handle advance time form field changes
     * @param {string} field - Field name
     * @param {any} value - New value
     */
    const handleAdvanceTimeFormChange = (field, value) => {
        setAdvanceTimeForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    /**
     * Save advance time settings for an event type
     * @param {string} eventKey - Event type key
     */
    const saveAdvanceTimeSettings = async (eventKey) => {
        try {
            setSavingAdvanceTime(prev => ({ ...prev, [eventKey]: true }));

            const payload = {
                supports_advance_time: advanceTimeForm.supports_advance_time,
                min_advance_minutes: advanceTimeForm.min_advance_minutes ? parseInt(advanceTimeForm.min_advance_minutes) : null,
                max_advance_minutes: advanceTimeForm.max_advance_minutes ? parseInt(advanceTimeForm.max_advance_minutes) : null,
                default_advance_minutes: advanceTimeForm.default_advance_minutes ? parseInt(advanceTimeForm.default_advance_minutes) : null
            };

            const response = await axios.put(
                `/api/notification-settings/${entityId}/event-types/${eventKey}/advance-time`,
                payload
            );

            if (response.data.success) {
                // Update local state
                const updateEventState = (events) => events.map(event =>
                    event.event_key === eventKey
                        ? {
                            ...event,
                            supports_advance_time: payload.supports_advance_time ? 1 : 0,
                            min_advance_minutes: payload.min_advance_minutes,
                            max_advance_minutes: payload.max_advance_minutes,
                            default_advance_minutes: payload.default_advance_minutes
                        }
                        : event
                );

                setEventTypes(updateEventState);

                // Update grouped events
                setGroupedEvents(prev => {
                    const updated = { ...prev };
                    Object.keys(updated).forEach(category => {
                        updated[category] = updateEventState(updated[category]);
                    });
                    return updated;
                });

                toast.success(t('advanceTimeSettingsSaved'));
                setExpandedEventKey(null);
                setAdvanceTimeForm({});

                // Clear cache and notify parent
                notificationService.clearCacheByPattern(`event_types_${entityId}`);
                if (typeof onEventTypesUpdate === 'function') {
                    await onEventTypesUpdate();
                }
            }
        } catch (error) {
            console.error('Error saving advance time settings:', error);
            if (error.response?.data?.error) {
                toast.error(error.response.data.error);
            } else {
                toast.error(t('errorSavingAdvanceTimeSettings'));
            }
        } finally {
            setSavingAdvanceTime(prev => ({ ...prev, [eventKey]: false }));
        }
    };

    /**
     * Get category icon
     */
    const getCategoryIcon = (category) => {
        switch (category) {
            case 'booking':
                return <Calendar className="w-5 h-5" />;
            case 'trip':
                return <Bell className="w-5 h-5" />;
            case 'account':
                return <User className="w-5 h-5" />;
            case 'system':
                return <Settings className="w-5 h-5" />;
            default:
                return <AlertTriangle className="w-5 h-5" />;
        }
    };

    /**
     * Get status badge
     */
    const getStatusBadge = (event) => {
        if (!event.system_active) {
            return (
                <span className={cn(
                    "px-2 py-1 text-xs rounded-full",
                    "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                )}>
                    <Lock className="w-3 h-3 inline mr-1" />
                    {t('systemDisabled')}
                </span>
            );
        }

        if (!event.is_enabled) {
            return (
                <span className={cn(
                    "px-2 py-1 text-xs rounded-full",
                    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                )}>
                    {t('entityDisabled')}
                </span>
            );
        }

        return (
            <span className={cn(
                "px-2 py-1 text-xs rounded-full",
                "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
            )}>
                {t('available')}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className={cn("p-4 rounded-lg", background.secondary, border.default, "border")}>
                <h3 className={cn("text-lg font-semibold mb-2", text.primary)}>
                    {t('notificationEventTypeManagement')}
                </h3>
                <p className={cn("text-sm", text.secondary)}>
                    {t('eventTypeManagementDescription')}
                </p>
                {/* <div className={cn("mt-3 p-3 rounded", background.primary, "border", border.default)}>
                    <div className="flex items-start space-x-2">
                        <Info className={cn("w-4 h-4 mt-0.5", text.accent)} />
                        <div className={cn("text-xs", text.secondary)}>
                            <p>{t('eventTypeHierarchyInfo')}</p>
                            <ul className="mt-2 space-y-1 ml-4">
                                <li>• {t('systemLevelControl')}</li>
                                <li>• {t('entityLevelControl')}</li>
                                <li>• {t('userLevelControl')}</li>
                            </ul>
                        </div>
                    </div>
                </div> */}
            </div>

            {/* Event Types by Category */}
            {Object.entries(groupedEvents).map(([category, events]) => (
                <ThemedCard key={category}>
                    <div className="p-4">
                        <div className="flex items-center mb-4">
                            {getCategoryIcon(category)}
                            <h4 className={cn("ml-2 font-semibold", text.primary)}>
                                {t(`eventCategory.${category}`)}
                            </h4>
                            <span className={cn("ml-auto text-sm", text.secondary)}>
                                {events.filter(e => e.is_available).length} / {events.length} {t('active')}
                            </span>
                        </div>

                        <div className="space-y-3">
                            {events.map(event => (
                                <div
                                    key={event.id}
                                    className={cn(
                                        "p-3 rounded-lg border",
                                        border.default,
                                        !event.system_active && "opacity-60"
                                    )}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3">
                                                <h5 className={cn("font-medium", text.primary)}>
                                                    {t(event.display_name_key)}
                                                </h5>
                                                {getStatusBadge(event)}
                                            </div>
                                            <p className={cn("text-sm mt-1", text.secondary)}>
                                                {t(event.description_key)}
                                            </p>
                                            {/* Advance time info display */}
                                            {event.supports_advance_time === 1 && event.min_advance_minutes && event.max_advance_minutes && (
                                                <p className={cn("text-xs mt-1", text.muted)}>
                                                    {t('supportsAdvanceTime', {
                                                        min: event.min_advance_minutes,
                                                        max: event.max_advance_minutes
                                                    })}
                                                </p>
                                            )}
                                            {/* Expand button for advance time settings - only for relevant event types */}
                                            {event.system_active && (
                                                event.supports_advance_time === 1 ||
                                                event.event_key.includes('approaching') ||
                                                event.event_key.includes('reminder')
                                            ) && (
                                                <button
                                                    onClick={() => toggleAdvanceTimeExpand(event)}
                                                    className={cn(
                                                        "flex items-center space-x-1 mt-2 text-xs",
                                                        "hover:underline",
                                                        text.accent
                                                    )}
                                                >
                                                    <Clock className="w-3 h-3" />
                                                    <span>{t('advanceTimeSettings')}</span>
                                                    {expandedEventKey === event.event_key ? (
                                                        <ChevronUp className="w-3 h-3" />
                                                    ) : (
                                                        <ChevronDown className="w-3 h-3" />
                                                    )}
                                                </button>
                                            )}
                                        </div>

                                        <button
                                            onClick={() => toggleEventType(event.event_key, event.is_enabled)}
                                            disabled={!event.system_active || toggling[event.event_key]}
                                            className={cn(
                                                "ml-4 p-2 rounded-lg transition-colors",
                                                event.system_active
                                                    ? "hover:bg-gray-100 dark:hover:bg-gray-700"
                                                    : "cursor-not-allowed opacity-50"
                                            )}
                                            title={
                                                !event.system_active
                                                    ? t('cannotToggleSystemDisabled')
                                                    : event.is_enabled
                                                    ? t('clickToDisable')
                                                    : t('clickToEnable')
                                            }
                                        >
                                            {toggling[event.event_key] ? (
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
                                            ) : event.is_enabled ? (
                                                <ToggleRight className={cn("w-6 h-6", "text-green-600")} />
                                            ) : (
                                                <ToggleLeft className={cn("w-6 h-6", text.secondary)} />
                                            )}
                                        </button>
                                    </div>

                                    {/* Expandable Advance Time Settings Panel */}
                                    {expandedEventKey === event.event_key && (
                                        <div className={cn(
                                            "mt-3 pt-3 border-t",
                                            border.default
                                        )}>
                                            <div className="space-y-3">
                                                {/* Enable/Disable advance time toggle */}
                                                <div className="flex items-center justify-between">
                                                    <label className={cn("text-sm font-medium", text.primary)}>
                                                        {t('enableAdvanceTimeSetting')}
                                                    </label>
                                                    <button
                                                        onClick={() => handleAdvanceTimeFormChange(
                                                            'supports_advance_time',
                                                            !advanceTimeForm.supports_advance_time
                                                        )}
                                                        className="p-1"
                                                    >
                                                        {advanceTimeForm.supports_advance_time ? (
                                                            <ToggleRight className="w-6 h-6 text-green-600" />
                                                        ) : (
                                                            <ToggleLeft className={cn("w-6 h-6", text.secondary)} />
                                                        )}
                                                    </button>
                                                </div>

                                                {/* Time inputs - only show if enabled */}
                                                {advanceTimeForm.supports_advance_time && (
                                                    <div className="grid grid-cols-3 gap-3">
                                                        <div>
                                                            <label className={cn("block text-xs mb-1", text.secondary)}>
                                                                {t('minMinutes')}
                                                            </label>
                                                            <ThemedInput
                                                                type="number"
                                                                min="1"
                                                                value={advanceTimeForm.min_advance_minutes}
                                                                onChange={(e) => handleAdvanceTimeFormChange(
                                                                    'min_advance_minutes',
                                                                    e.target.value
                                                                )}
                                                                className="w-full text-sm"
                                                                placeholder="5"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className={cn("block text-xs mb-1", text.secondary)}>
                                                                {t('maxMinutes')}
                                                            </label>
                                                            <ThemedInput
                                                                type="number"
                                                                min="1"
                                                                value={advanceTimeForm.max_advance_minutes}
                                                                onChange={(e) => handleAdvanceTimeFormChange(
                                                                    'max_advance_minutes',
                                                                    e.target.value
                                                                )}
                                                                className="w-full text-sm"
                                                                placeholder="30"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className={cn("block text-xs mb-1", text.secondary)}>
                                                                {t('defaultMinutes')}
                                                            </label>
                                                            <ThemedInput
                                                                type="number"
                                                                min="1"
                                                                value={advanceTimeForm.default_advance_minutes}
                                                                onChange={(e) => handleAdvanceTimeFormChange(
                                                                    'default_advance_minutes',
                                                                    e.target.value
                                                                )}
                                                                className="w-full text-sm"
                                                                placeholder="10"
                                                            />
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Save button */}
                                                <div className="flex justify-end">
                                                    <ThemedButton
                                                        onClick={() => saveAdvanceTimeSettings(event.event_key)}
                                                        disabled={savingAdvanceTime[event.event_key]}
                                                        className="text-sm"
                                                    >
                                                        {savingAdvanceTime[event.event_key] ? (
                                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                                                        ) : (
                                                            <>
                                                                <Save className="w-4 h-4 mr-1" />
                                                                {t('save')}
                                                            </>
                                                        )}
                                                    </ThemedButton>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </ThemedCard>
            ))}

            {eventTypes.length === 0 && (
                <ThemedCard>
                    <div className="p-8 text-center">
                        <AlertTriangle className={cn("w-12 h-12 mx-auto mb-4", text.muted)} />
                        <p className={cn("text-lg", text.secondary)}>
                            {t('noEventTypesFound')}
                        </p>
                    </div>
                </ThemedCard>
            )}
        </div>
    );
};

export default NotificationEventTypeManager;
